import { useEffect, useState } from 'react'

import { useCreateFacility } from '../hooks/useCreateFacility'
import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HiOutlineCheckCircle, HiOutlineOfficeBuilding } from 'react-icons/hi'

import {
  Box,
  Grid,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useToast,
} from '@chakra-ui/react'

import { Modal } from '@/components/overlays'
import { Button } from '@/components/ui'
import { CreateFacilityInput, FacilityType } from '@/graphql/generated/schemas'
import { tVar } from '@/utils/i18n'

import { FormInputsIProps } from '../types/types'
import { CSVParseResultI, CSV_UploadStatus } from '../utils/csvParser'
import {
  getErrorMessage,
  getFacilityTimezone,
  isBulkUploadButtonDisabled,
} from '../utils/utils'
import { BulkUploadFacilitiesForm } from './BulkUploadFacilitiesForm'
import { CreateFacilityForm } from './CreateFacilityForm'

interface IProps {
  isOpen: boolean
  onClose: () => void
}
const BULK_UPLOAD_TAB_INDEX = 1

export const CreateFacilityModal = ({ isOpen, onClose }: IProps) => {
  const { t } = useTranslation()
  const form = useForm<FormInputsIProps>({
    mode: 'onChange',
  })
  const { handleSubmit, formState, watch } = form
  const { isValid, isSubmitting } = formState
  const type = watch('type')?.value
  const { showSuccess, showError } = useToasts()
  const { create, facilityTypes } = useCreateFacility()
  const [parseResult, setParseResult] = useState<CSVParseResultI | null>(null)
  const [tabIndex, setTabIndex] = useState(0)
  const [isCreating, setIsCreating] = useState(false)
  const [isBulkUploadComplete, setBulkUploadIsComplete] = useState(false)

  const isBulkUpload = tabIndex === BULK_UPLOAD_TAB_INDEX

  const toast = useToast()

  /**
   * Handles bulk facility creation from parsed CSV data
   */
  const handleBulkCreate = async () => {
    if (!parseResult || parseResult.data.length === 0) {
      showError(t('facilities:crud_no_facilities_to_create'))
      return
    }

    setIsCreating(true)
    const results = {
      successful: [] as string[],
      failed: [] as { name: string; error: string }[],
    }

    // Initialize all facilities with pending status
    const facilitiesWithStatus = parseResult.data.map((facility) => ({
      ...facility,
      uploadStatus: CSV_UploadStatus.Pending,
    }))
    setParseResult({ ...parseResult, data: facilitiesWithStatus })

    for (let i = 0; i < facilitiesWithStatus.length; i++) {
      const facility = facilitiesWithStatus[i]
      const input: CreateFacilityInput = {
        name: facility.name,
        shortName: facility.shortName.toLocaleUpperCase(),
        type: FacilityType.Normal,
        address: facility.address ? [facility.address] : null,
        latitude: parseFloat(facility.lat),
        longitude: parseFloat(facility.lon),
        timeZone: facility.timezone,
      }

      try {
        await create(input)
        results.successful.push(facility.name)

        // Update status to success
        setParseResult((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            data: prev.data.map((f, index) =>
              index === i ? { ...f, uploadStatus: CSV_UploadStatus.Success } : f
            ),
          }
        })
      } catch (e) {
        results.failed.push({
          name: facility.name,
          error: e.message,
        })

        // Update status to failed with error
        setParseResult((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            data: prev.data.map((f, index) =>
              index === i
                ? {
                    ...f,
                    uploadStatus: CSV_UploadStatus.Failed,
                    uploadError: e.message,
                  }
                : f
            ),
          }
        })
      }
    }

    setIsCreating(false)
    setBulkUploadIsComplete(true)

    // Show results
    if (results.successful.length > 0) {
      const numberCreated = results.successful.length

      toast({
        title: t('facilities:crud_bulk_success_title'),
        description: tVar(t, 'facilities:crud_bulk_success_description', {
          count: numberCreated,
        }),
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      })
    }

    if (results.failed.length > 0) {
      const numberFailed = results.failed.length

      toast({
        title: t('common:error_label'),
        description: tVar(t, 'facilities:crud_bulk_error_description', {
          count: numberFailed,
        }),
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      })
    }
  }

  /**
   * Handles individual facility creation
   */
  const onSubmit: SubmitHandler<FormInputsIProps> = async (values) => {
    const input: CreateFacilityInput = {
      name: values.name,
      shortName: values.shortName.toLocaleUpperCase(),
      type: values.type?.value,
      address: values?.address ? [values.address] : null,
      latitude: parseFloat(values.latitude),
      longitude: parseFloat(values.longitude),
      ...(type !== FacilityType.Market && {
        timeZone: getFacilityTimezone(values.latitude, values.longitude),
      }),
    }
    try {
      await create(input)
      const successMsg = tVar(t, 'facilities:crud_success_created_facility', {
        name: values.name,
      })
      showSuccess(successMsg)
      onClose()
    } catch (e) {
      showError(getErrorMessage(e, values.name, 'creating'))
    }
  }

  /**
   * Handles form submission based on active tab
   */
  const handleFormSubmit = async () => {
    if (isBulkUpload) {
      // When bulk submit is completed the Submit turns into a Done button
      if (isBulkUploadComplete) {
        onClose()
      } else {
        await handleBulkCreate()
      }
    } else {
      await handleSubmit(onSubmit)()
    }
  }

  const handleClose = () => {
    if (!isSubmitting && !isCreating) {
      onClose()
    }
  }

  useEffect(() => {
    if (!parseResult) {
      setBulkUploadIsComplete(false)
    }
  }, [parseResult])

  // We want the modal to be larger when somebody is viewing the Facilities
  // that have been parsed and are in the table
  const modalMaxWidth =
    isBulkUpload && parseResult?.data?.length ? '90%' : '700px'
  const modalMaxHeight =
    isBulkUpload && parseResult?.data?.length ? '90%' : 'auto'

  const getCreateFacilityButtonTitle = () => {
    if (isBulkUploadComplete) {
      return t('common:done')
    }
    return isBulkUpload
      ? t('facilities:crud_create_facilities_button')
      : t('facilities:crud_create_facility_button')
  }

  return (
    <Modal
      closeOnEsc={false}
      closeOnOverlayClick={false}
      isCentered
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={handleClose}
      size='4xl'
    >
      <ModalOverlay />
      <ModalContent bg='#fff' maxH={modalMaxHeight} maxW={modalMaxWidth}>
        <Box px='10' py='6'>
          <Grid gap='10px' gridTemplateColumns='30px 1fr'>
            <HiOutlineOfficeBuilding size='30px' />
            <Box>
              <Box
                color='#353849'
                fontSize='26px'
                fontWeight='extrabold'
                letterSpacing='-0.4px'
                lineHeight='1'
                mb='3'
              >
                {t('facilities:crud_create_title')}
              </Box>
            </Box>
          </Grid>
        </Box>
        <ModalCloseButton color='#000' opacity={0.4} />

        <ModalBody overflowY='auto' p='0'>
          <Tabs
            bgColor='#FFFFFF'
            borderColor='transparent'
            fontWeight='500'
            index={tabIndex}
            isLazy
            onChange={(index) => setTabIndex(index)}
          >
            <TabList px='24px'>
              <Tab
                _selected={{
                  color: 'components.tab.active',
                  borderBottomColor: 'components.tab.active',
                  opacity: 1,
                }}
                opacity={0.4}
              >
                <Box alignItems='center' display='inline-flex'>
                  {t('facilities:crud_tab_create_individual')}
                </Box>
              </Tab>
              <Tab
                _selected={{
                  color: 'components.tab.active',
                  borderBottomColor: 'components.tab.active',
                  opacity: 1,
                }}
                opacity={0.4}
              >
                <Box alignItems='center' display='inline-flex'>
                  {t('facilities:crud_tab_bulk_upload')}
                </Box>
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel m={0} mt='5px' px='0'>
                <CreateFacilityForm
                  facilityTypes={facilityTypes}
                  form={form}
                  isOpen={isOpen}
                />
              </TabPanel>
              <TabPanel mt='5px' px='0'>
                <BulkUploadFacilitiesForm
                  form={form}
                  onParseResultChange={setParseResult}
                  parseResult={parseResult}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter py='4'>
          {!isBulkUploadComplete && (
            <Button mr='3' onClick={onClose} variant='secondary'>
              {t('common:cancel')}
            </Button>
          )}
          <Button
            isDisabled={
              isBulkUpload ? isBulkUploadButtonDisabled(parseResult) : !isValid
            }
            isLoading={isSubmitting || isCreating}
            leftIcon={<HiOutlineCheckCircle size='24px' />}
            loadingText={
              isBulkUpload
                ? t('facilities:crud_loading_creating_facilities')
                : t('facilities:crud_loading_creating_facility')
            }
            onClick={handleFormSubmit}
          >
            {getCreateFacilityButtonTitle()}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
